import { formatMoney, convertMoneyToMinorUnits } from '@theme/money-formatting';

/**
 * Syncs a dual-handle price slider with the existing price-facet text inputs.
 */
class PriceSlider {
  /** @type {HTMLElement} */
  container;
  /** @type {HTMLElement | null} */
  component;
  /** @type {HTMLInputElement | null} */
  minInput;
  /** @type {HTMLInputElement | null} */
  maxInput;
  /** @type {HTMLElement | null} */
  range;
  /** @type {HTMLElement | null} */
  minLabel;
  /** @type {HTMLElement | null} */
  maxLabel;
  /** @type {HTMLInputElement | null} */
  textMinInput;
  /** @type {HTMLInputElement | null} */
  textMaxInput;
  /** @type {string} */
  currency;
  /** @type {string} */
  moneyFormat;
  /** @type {number} */
  absoluteMax;

  /**
   * @param {HTMLElement} container - The element with [data-price-slider]
   */
  constructor(container) {
    this.container = container;

    /** @type {HTMLElement | null} */
    this.component = /** @type {HTMLElement | null} */ (container.closest('price-facet-component'));

    /** @type {HTMLInputElement | null} */
    this.minInput = /** @type {HTMLInputElement | null} */ (
      container.querySelector('[data-slider-input="min"]')
    );
    /** @type {HTMLInputElement | null} */
    this.maxInput = /** @type {HTMLInputElement | null} */ (
      container.querySelector('[data-slider-input="max"]')
    );
    /** @type {HTMLElement | null} */
    this.range = /** @type {HTMLElement | null} */ (container.querySelector('[data-slider-range]'));
    /** @type {HTMLElement | null} */
    this.minLabel = /** @type {HTMLElement | null} */ (
      container.querySelector('[data-slider-min-label]')
    );
    /** @type {HTMLElement | null} */
    this.maxLabel = /** @type {HTMLElement | null} */ (
      container.querySelector('[data-slider-max-label]')
    );

    if (!this.minInput || !this.maxInput || !this.range) return;

    /** @type {HTMLInputElement | null} */
    this.textMinInput = /** @type {HTMLInputElement | null} */ (
      this.component?.querySelector('[ref="minInput"]') ?? null
    );
    /** @type {HTMLInputElement | null} */
    this.textMaxInput = /** @type {HTMLInputElement | null} */ (
      this.component?.querySelector('[ref="maxInput"]') ?? null
    );

    const rawCurrency = this.component?.dataset.currency;
    this.currency = rawCurrency ?? 'USD';

    const rawMoneyFormat = this.component?.dataset.moneyFormat ?? '{{amount}}';
    this.moneyFormat = this.#extractMoneyPlaceholder(rawMoneyFormat);
    this.absoluteMax = parseInt(this.maxInput.max, 10) || 0;

    this.minInput.addEventListener('input', this.#onSliderInput);
    this.minInput.addEventListener('change', this.#onSliderChange);
    this.maxInput.addEventListener('input', this.#onSliderInput);
    this.maxInput.addEventListener('change', this.#onSliderChange);

    if (this.textMinInput) {
      this.textMinInput.addEventListener('change', this.#onTextInputChange);
    }
    if (this.textMaxInput) {
      this.textMaxInput.addEventListener('change', this.#onTextInputChange);
    }

    this.#updateUI();
  }

  /**
   * @param {string} format
   * @returns {string}
   */
  #extractMoneyPlaceholder(format) {
    const match = format.match(/{{\s*\w+\s*}}/);
    return match ? match[0] : '{{amount}}';
  }

  /**
   * Updates text inputs and UI while dragging, without triggering a filter update.
   * @param {Event} event
   */
  #onSliderInput = (event) => {
    event.stopPropagation();

    if (!this.minInput || !this.maxInput) return;

    let min = parseInt(this.minInput.value, 10) || 0;
    let max = parseInt(this.maxInput.value, 10) || 0;

    // Keep min <= max
    if (event.target === this.minInput && min > max) {
      max = min;
      this.maxInput.value = String(max);
    } else if (event.target === this.maxInput && max < min) {
      min = max;
      this.minInput.value = String(min);
    }

    this.#syncTextInputs(min, max, false);
    this.#updateUI();
  };

  /**
   * Triggers the filter update once the user releases a slider handle.
   * @param {Event} event
   */
  #onSliderChange = (event) => {
    event.stopPropagation();
    this.#triggerFilterUpdate();
  };

  #onTextInputChange = () => {
    this.#syncFromTextInputs();
  };

  /**
   * Updates slider values from the text inputs.
   */
  #syncFromTextInputs() {
    if (!this.minInput || !this.maxInput) return;

    const minValue = this.textMinInput ? this.textMinInput.value : '';
    const maxValue = this.textMaxInput ? this.textMaxInput.value : '';

    const minCents = minValue
      ? convertMoneyToMinorUnits(minValue, this.currency) ?? 0
      : 0;
    const maxCents = maxValue
      ? convertMoneyToMinorUnits(maxValue, this.currency) ?? this.absoluteMax
      : this.absoluteMax;

    this.minInput.value = String(minCents);
    this.maxInput.value = String(maxCents);
    this.#updateUI();
  }

  /**
   * Writes slider values back to the existing text inputs.
   * @param {number} minCents
   * @param {number} maxCents
   * @param {boolean} [triggerFilter=true] - Whether to trigger a collection filter update.
   */
  #syncTextInputs(minCents, maxCents, triggerFilter = true) {
    if (!this.textMinInput || !this.textMaxInput) return;

    this.textMinInput.value = formatMoney(minCents, this.moneyFormat, this.currency);
    this.textMaxInput.value = formatMoney(maxCents, this.moneyFormat, this.currency);

    if (triggerFilter) {
      this.#triggerFilterUpdate();
    }
  }

  /**
   * Triggers the existing price-facet update method.
   */
  #triggerFilterUpdate() {
    const component = /** @type {any} */ (this.component);
    if (this.component && typeof component.updatePriceFilterAndResults === 'function') {
      component.updatePriceFilterAndResults();
      return;
    }

    // Fallback: dispatch a change event on the component so on:change handlers run
    if (this.component) {
      this.component.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /**
   * Updates the visual range highlight and labels.
   */
  #updateUI() {
    if (!this.minInput || !this.maxInput || !this.range) return;

    const min = parseInt(this.minInput.value, 10) || 0;
    const max = parseInt(this.maxInput.value, 10) || 0;

    const minPercent = this.absoluteMax ? (min / this.absoluteMax) * 100 : 0;
    const maxPercent = this.absoluteMax ? (max / this.absoluteMax) * 100 : 0;

    this.range.style.left = `${minPercent}%`;
    this.range.style.width = `${maxPercent - minPercent}%`;

    if (this.minLabel) {
      this.minLabel.textContent = formatMoney(min, this.moneyFormat, this.currency);
    }
    if (this.maxLabel) {
      this.maxLabel.textContent = formatMoney(max, this.moneyFormat, this.currency);
    }
  }
}

function initPriceSliders() {
  document.querySelectorAll('[data-price-slider]').forEach((slider) => {
    if (!(slider instanceof HTMLElement)) return;
    if (slider.dataset.priceSliderInitialized) return;
    slider.dataset.priceSliderInitialized = 'true';
    new PriceSlider(slider);
  });
}

/**
 * Checks whether any added nodes contain an uninitialized price slider.
 * @param {MutationRecord[]} mutations
 * @returns {boolean}
 */
function shouldReInitSliders(mutations) {
  return mutations.some((mutation) =>
    Array.from(mutation.addedNodes).some((node) => {
      if (!(node instanceof HTMLElement)) return false;
      if (node.matches('[data-price-slider]') && !node.dataset.priceSliderInitialized) return true;
      return node.querySelector('[data-price-slider]:not([data-price-slider-initialized])') !== null;
    })
  );
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initPriceSliders);
} else {
  initPriceSliders();
}

// Re-init only when a new price slider is injected (e.g., after Section Rendering API updates)
const priceSliderObserver = new MutationObserver((mutations) => {
  if (shouldReInitSliders(mutations)) {
    initPriceSliders();
  }
});
priceSliderObserver.observe(document.body, { childList: true, subtree: true });
